import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthedFetch, parseJsonOrThrow, throwIfNotOk } from './client';
import type { GroupName, OverrideKind } from '../domain';
import { z } from 'zod';

const Empty = z.object({}).passthrough();

function invalidateState(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['state'] });
}

export function useSetSetpointMutation() {
  const fetcher = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { setpoint_c: number }) => {
      const res = await fetcher(`/setpoint`, {
        method: 'POST',
        body: JSON.stringify({ setpoint_c: vars.setpoint_c }),
      });
      return parseJsonOrThrow(res, 'POST /setpoint', (raw) => Empty.parse(raw));
    },
    onSuccess: () => invalidateState(qc),
  });
}

export function useSetWpmOffsetMutation() {
  const fetcher = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { wpm_offset_c: number }) => {
      const res = await fetcher(`/wpm-offset`, {
        method: 'POST',
        body: JSON.stringify({ wpm_offset_c: vars.wpm_offset_c }),
      });
      return parseJsonOrThrow(res, 'POST /wpm-offset', (raw) => Empty.parse(raw));
    },
    onSuccess: () => invalidateState(qc),
  });
}

export function useSetOffsetMutation() {
  const fetcher = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { group: GroupName; offset_c: number }) => {
      const res = await fetcher(`/groups/${vars.group}/offset`, {
        method: 'POST',
        body: JSON.stringify({ offset_c: vars.offset_c }),
      });
      return parseJsonOrThrow(res, 'POST /groups/{group}/offset', (raw) => Empty.parse(raw));
    },
    onSuccess: () => invalidateState(qc),
  });
}

export function useSetBypassZoneMutation() {
  const fetcher = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (group: GroupName) => {
      const res = await fetcher('/bypass', { method: 'POST', body: JSON.stringify({ group }) });
      return parseJsonOrThrow(res, 'POST /bypass', (raw) => Empty.parse(raw));
    },
    onSuccess: () => invalidateState(qc),
  });
}

export function useApplyOverrideMutation() {
  const fetcher = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { group: GroupName; kind: OverrideKind; duration_ms: number }) => {
      const res = await fetcher(`/groups/${vars.group}/override`, {
        method: 'POST',
        body: JSON.stringify({ kind: vars.kind, duration_ms: vars.duration_ms }),
      });
      return parseJsonOrThrow(res, 'POST /groups/{group}/override', (raw) => Empty.parse(raw));
    },
    onSuccess: () => invalidateState(qc),
  });
}

export function useAckFaultMutation() {
  const fetcher = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetcher(`/faults/${encodeURIComponent(id)}/ack`, { method: 'POST' });
      await throwIfNotOk(res, 'POST /faults/{id}/ack');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['faults'] }),
  });
}

export function useClearOverrideMutation() {
  const fetcher = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (group: GroupName) => {
      const res = await fetcher(`/groups/${group}/override`, { method: 'DELETE' });
      await throwIfNotOk(res, `DELETE /groups/${group}/override`);
    },
    onSuccess: () => invalidateState(qc),
  });
}

export function useSetWeatherLocationMutation() {
  const fetcher = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { location: { lat: number; lon: number } | null }) => {
      const res = await fetcher(`/weather-location`, {
        method: 'POST',
        body: JSON.stringify({ location: vars.location }),
      });
      return parseJsonOrThrow(res, 'POST /weather-location', (raw) => Empty.parse(raw));
    },
    onSuccess: () => invalidateState(qc),
  });
}
